import {
    SCORE_THRESHOLD_HIGH,
    SCORE_THRESHOLD_MEDIUM,
    SCORE_THRESHOLD_LOW,
} from "../const/numbers"

export async function getUserLocation() {
    consoleLog("Getting user location.")

    if (!navigator.geolocation) {
        return {
            error: {
                type: "UNAVAILABLE",
                message: "Geolocation is not supported by this browser.",
            },
        }
    }

    return new Promise((resolve, reject) => {
        const options = {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                let locationData = {}

                if (!position.timestamp) {
                    locationData = {
                        error: {
                            type: "UNAVAILABLE",
                            message: "Location data is unavailable.",
                        },
                    }
                } else {
                    locationData = {
                        coordinates: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        },
                        timestamp: position.timestamp,
                    }
                }

                consoleLog("Location data:", locationData)
                resolve(locationData)
            },
            (error) => {
                const denied = error?.code === error?.PERMISSION_DENIED
                resolve({
                    error: {
                        type: denied ? "DENIED" : "UNAVAILABLE",
                        message: denied
                            ? "Location access was denied. Please enable location services to use this feature."
                            : "We couldn't get a location fix from your browser. Please try again.",
                    },
                })
            },
            options,
        )
    })
    // TODO: Explore a way to preceed this with a Toast notification heads-up and button
}

export function signUpClickHandler() {
    // Navigate to registration page
    window.location.href = "/user/register"
}

export function comingSoonClickHandler() {
    const comingSoonDialog = document.getElementById("signUpDetails")
    if (comingSoonDialog) {
        comingSoonDialog.showModal()
    } else {
        console.error("Error: Coming Soon modal not loaded.")
    }
}

export function riskProfileClickHandler() {
    const riskProfileDialog = document.getElementById("riskProfileDetails")
    if (riskProfileDialog) {
        riskProfileDialog.showModal()
    } else {
        console.error("Error: Risk Profile modal not loaded.")
    }
}

export function colorCode(value, format = "class") {
    if (format === "hex") {
        if (value >= SCORE_THRESHOLD_HIGH) return "#198754" // success
        if (value >= SCORE_THRESHOLD_MEDIUM) return "#0dcaf0" // info
        if (value >= SCORE_THRESHOLD_LOW) return "#ffc107" // warning
        return "#dc3545" // danger
    }

    // default bootstrap class names
    if (value >= SCORE_THRESHOLD_HIGH) return "success"
    if (value >= SCORE_THRESHOLD_MEDIUM) return "info"
    if (value >= SCORE_THRESHOLD_LOW) return "warning"
    return "danger"
}

export function riskProfile(value, format = "words") {
    if (format === "icons") {
        if (value >= SCORE_THRESHOLD_HIGH)
            return <i className="bi bi-hand-thumbs-up"></i> // success
        if (value >= SCORE_THRESHOLD_MEDIUM)
            return <i className="bi bi-info-circle"></i> // info
        if (value >= SCORE_THRESHOLD_LOW)
            return <i className="bi bi-exclamation-triangle"></i> // warning
        return <i className="bi bi-flag"></i> // danger
    }

    if (value >= SCORE_THRESHOLD_HIGH) return "safe"
    if (value >= SCORE_THRESHOLD_MEDIUM) return "okay"
    if (value >= SCORE_THRESHOLD_LOW) return "caution"
    return "danger"
}

export function safetyProfile(value, format = "assessment") {
    if (value >= SCORE_THRESHOLD_HIGH) return "High"
    if (value >= SCORE_THRESHOLD_MEDIUM) return "Moderate"
    if (value >= SCORE_THRESHOLD_LOW) return "Low"
    return "Dangerous"
}

/**
 * Display name from first/last name parts; empty when neither exists so
 * callers can fall back to the username (mirrors the Android app).
 */
export function buildFullName(firstName, lastName) {
    return [firstName, lastName]
        .map((part) => (typeof part === "string" ? part.trim() : ""))
        .filter(Boolean)
        .join(" ")
}

/**
 * Coarse "5 minutes ago" style label for a past timestamp; "" when the value
 * is missing or unparseable.
 */
export function formatRelativeTime(value) {
    const time = new Date(value).getTime()
    if (!value || Number.isNaN(time)) return ""

    const seconds = Math.max(0, Math.round((Date.now() - time) / 1000))
    const units = [
        ["day", 86400],
        ["hour", 3600],
        ["minute", 60],
    ]
    for (const [unit, size] of units) {
        if (seconds >= size) {
            const count = Math.floor(seconds / size)
            return `${count} ${unit}${count === 1 ? "" : "s"} ago`
        }
    }
    return "Just now"
}

export function safetyPercentage(value) {
    return `${((value / 20) * 100).toFixed(0)}`
}

/**
 * Redirects the user to a profile page based on the provided place data.
 *
 * This function generates a URL-friendly slug from the formatted address in the
 * `placeData` object and uses the `navigate` function to redirect the user to
 * the corresponding profile page. If the required data is missing, it logs an
 * error message instead.
 *
 * @param {Object} placeData - The data object containing information about the place.
 * @param {Object} [placeData.fromGoogle] - Nested object containing Google-specific data.
 * @param {string} [placeData.fromGoogle.formatted_address] - The formatted address of the place.
 * @param {string} placeData.id - The unique identifier for the place.
 * @param {Function} navigate - A function to handle navigation (e.g., from a router library).
 */
export function redirectProfileSlug(placeData, navigate) {
    if (!placeData) {
        consoleLog("Error: No place data provided for profile slugging.")
        return
    }

    let placeSlug = placeData?.slug || placeData?.fromGoogle?.formatted_address

    if (placeSlug) {
        let entityPath = `/analysis/${placeSlug}`
        consoleLog(`Address should be: ${entityPath}`)
        navigate(`${entityPath}`, { replace: true })
    } else {
        consoleLog(
            "Error: No formatted address found in place data for profile slugging.",
        )
    }
}

export function consoleLog(...args) {
    if (import.meta.env.MODE !== "production") {
        console.log(...args)
    }
}

/**
 * Creates a debounced version of a function that delays execution until after
 * the specified delay has passed since the last call.
 *
 * Useful for rate-limiting event handlers, especially for WebSocket operations.
 * For example, debouncing refresh handlers to prevent too many simultaneous requests.
 *
 * Security Use: Prevents abuse of real-time event handlers (Fix #3)
 * Performance Use: Reduces unnecessary API calls during rapid events
 *
 * @param {Function} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} - The debounced function
 *
 * @example
 * // Debounce a refresh handler to max 1 call per second
 * const debouncedRefresh = debounce((communityId) => {
 *   fetchCommunityPlaces(communityId)
 * }, 1000)
 *
 * // Call it many times rapidly, but it will only execute once per second
 * debouncedRefresh(123)
 * debouncedRefresh(123)
 * debouncedRefresh(123) // Only this final call will execute after 1s delay
 */
export function debounce(func, delay) {
    let timeoutId

    return function debounced(...args) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
            func.apply(this, args)
        }, delay)
    }
}
